import React from 'react';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { Vector, Option } from 'prelude-ts';

import { RuleExecutor, AxiomRule, Negation } from '../Rule';
import Formula from './Formula';
import Statement from '../Statement';

import styles from '../styles/Formula.module.css';

class Atom extends Formula {
  toTex(): string {
    return (this.isNegated ? '\\neg ' : '') + this.name;
  }
  readonly name: string;
  readonly isNegated: boolean;

  constructor(name: string, isNegated: boolean = false) {
    super();
    this.name = name;
    this.isNegated = isNegated;
  }

  negated(): Formula {
    return new Atom(this.name, !this.isNegated);
  }

  equals(that: Formula): boolean {
    return (
      that instanceof Atom &&
      this.name === that.name &&
      this.isNegated === that.isNegated
    );
  }

  subFormulae(): Vector<Formula> {
    return Vector.empty();
  }

  /**
   * CSS class for the atom image, if any.
   */
  private getImageClass(): string | undefined {
    switch (this.name) {
      case 'A':
        return styles.A;
      case 'B':
        return styles.B;
      case 'C':
        return styles.C;
      case 'D':
        return styles.D;
      default:
        return undefined;
    }
  }

  /**
   * Render the atom as an image, if any. Otherwise, render it as text.
   */
  private getImage(isRequirement: boolean): JSX.Element {
    const isInverted = this.isNegated !== isRequirement;

    if (this.getImageClass() !== undefined) {
      return (
        <div
          className={classNames(styles.image, this.getImageClass(), {
            [styles.inverted]: isInverted
          })}
        />
      );
    } else {
      return (
        <div
          className={classNames(styles.text, { [styles.inverted]: isInverted })}
        >
          <Typography align="center" inline>
            {this.isNegated ? '¬' : ''}
            {this.name}
          </Typography>
        </div>
      );
    }
  }

  render(subgoal: Statement, executor: RuleExecutor): JSX.Element {
    const isRoot = subgoal.hasFormula(this.id);
    const isRequirement = subgoal.hasRequirement(this.id, true);
    const dropTarget = subgoal.requirements.find((f: Formula) => {
      return this.equals(f);
    });
    const canEliminate = !isRequirement && dropTarget.isSome();
    const enabled = isRoot && (this.isNegated || canEliminate);

    return (
      <div
        className={classNames(styles.formula, styles.atom, {
          [styles.active]: enabled
        })}
        onClick={this.getClickMethod(
          enabled,
          canEliminate,
          dropTarget,
          executor
        )}
      >
        {this.getImage(isRequirement)}
      </div>
    );
  }

  private getClickMethod(
    enabled: boolean,
    canEliminate: boolean,
    target: Option<Formula>,
    executor: RuleExecutor
  ) {
    if (enabled) {
      if (canEliminate) {
        return () => executor(new AxiomRule(this, target.getOrThrow()));
      } else {
        return () => executor(new Negation(this));
      }
    } else {
      return undefined;
    }
  }
}

export default Atom;
